/**
 * El proyecto en el que estás, y el cambio a otro, al pie del menú.
 *
 * Estaba en la barra de arriba, dentro del migajero, y de ahí se mudó por lo que
 * es: el proyecto no es una posición dentro de una jerarquía —que es lo que un
 * migajero dice— sino el CONTEXTO de todo lo que hay debajo. El menú entero
 * cambia de contenido según cuál esté elegido, así que su selector pertenece al
 * menú y no a la línea de arriba.
 *
 * Va abajo y no arriba, encima de la cuenta: las dos cosas que dicen «con qué
 * identidad y sobre qué estoy trabajando» quedan juntas en la misma esquina, y
 * la parte de arriba del menú se queda entera para navegar.
 *
 * El desplegable se abre HACIA ARRIBA por el mismo motivo por el que está aquí:
 * debajo no hay sitio.
 */
import { useEffect, useRef, useState } from 'react'
import { IconCheck, IconChevronDown, IconPlus, IconRotate2, IconSelector, IconTrash } from '@tabler/icons-react'
import { roleLabelKey, type ProjectAccessRole } from '@webbuilder/shared-types'
import { roleIcon } from '../../lib/roleIcons'
import { useI18n } from '../../lib/i18n'

export type SidebarProject = {
  id: string
  name: string
  /** Qué eres en él. Ausente hablando con un worker anterior al equipo. */
  role?: ProjectAccessRole
  /** Su favicon, para que cada fila del selector tenga su cara. */
  faviconLight?: string
  faviconDark?: string
}

/**
 * La cara de un proyecto: su favicon, o su inicial si no tiene.
 *
 * Los dos favicons se dibujan y decide la hoja de estilos. No se lee el tema
 * desde React porque vive en una cookie compartida entre el panel y el editor y
 * puede cambiarlo otra pestaña: un valor leído una vez se queda viejo.
 *
 * Con uno solo puesto se usa ése en ambos temas —es lo que el cliente eligió— y
 * sin ninguno queda la letra, que a este tamaño distingue mejor que un icono
 * genérico repetido en todas las filas.
 */
function ProjectFace({ name, light, dark, className }: {
  name: string
  light?: string | undefined
  dark?: string | undefined
  className: string
}) {
  const has = Boolean(light || dark)
  if (!has) {
    return (
      <span className={className}>
        <span aria-hidden="true">{(name.trim().charAt(0) || 'P').toLocaleUpperCase('es')}</span>
      </span>
    )
  }
  return (
    <span className={`${className} has-favicon`}>
      {light ? (
        <img className={`dash-sidebar-project-favicon${dark ? ' is-light' : ''}`} src={light} alt="" />
      ) : null}
      {dark ? (
        <img className={`dash-sidebar-project-favicon${light ? ' is-dark' : ''}`} src={dark} alt="" />
      ) : null}
    </span>
  )
}

/**
 * Qué eres en un proyecto, en un símbolo.
 *
 * Corona si es tuyo, y si no el del rol que te dieron. Va sobre la marca de la
 * inicial y no en una columna aparte porque la respuesta pertenece al proyecto,
 * no a una propiedad suya que se lista al lado: pegado, se lee de una sola
 * mirada junto con la letra.
 *
 * De color, sólo la corona. Los seis roles comparten gris: aquí la pregunta que
 * se responde recorriendo la lista es «¿cuáles son míos?», que es binaria, y
 * seis tonos distintos la convertían en un código que hay que aprender. El tono
 * del catálogo se queda donde compara roles entre ellos, que es la pantalla de
 * Equipo.
 *
 * Sin `role` no se pinta nada. Es lo correcto y no un hueco: significa que
 * todavía no se sabe —la lista aún cargando, o una caché anterior a esta
 * función— e inventar una corona ahí sería afirmar que el proyecto es tuyo sin
 * haberlo comprobado.
 */
function RoleMark({ role }: { role: ProjectAccessRole | undefined }) {
  const { t } = useI18n()
  if (!role) return null
  const Icon = roleIcon(role)
  return (
    <span className={`dash-sidebar-project-role${role === 'owner' ? ' is-owner' : ''}`} title={t(roleLabelKey(role))}>
      <Icon size={10} stroke={2.4} aria-hidden="true" />
      <span className="dash-sr-only">{t(roleLabelKey(role))}</span>
    </span>
  )
}

/** Uno en su ventana de gracia, tal como lo enseña el selector. */
export type SidebarDeletedProject = { id: string; name: string; purgeAt: string | null }

/** Cuántos días quedan, redondeando hacia arriba: «medio día» se lee como cero. */
function daysLeft(purgeAt: string | null): number | null {
  if (!purgeAt) return null
  return Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / 86_400_000))
}

/** El plazo de un borrado, en palabras. */
function useDeadlineLine(purgeAt: string | null): string {
  const { t } = useI18n()
  const left = daysLeft(purgeAt)
  if (left === null) return t('sidebar.project.deleteSoon')
  if (left === 0) return t('sidebar.project.deleteToday')
  if (left === 1) return t('sidebar.project.deleteOne')
  return t('sidebar.project.deleteMany', { count: left })
}

/**
 * Un borrado en la lista: apagado, con su cuenta atrás y el gesto de recuperar.
 *
 * No entra al proyecto —no se puede— sino que abre el diálogo que explica en
 * qué estado está y ofrece traerlo de vuelta.
 */
function DeletedProjectRow({ project, onOpen }: {
  project: SidebarDeletedProject
  onOpen: () => void
}) {
  const { t } = useI18n()
  const deadline = useDeadlineLine(project.purgeAt)
  return (
    <button
      className="dash-sidebar-project-option is-deleted"
      type="button"
      role="menuitem"
      onClick={onOpen}
    >
      <span className="dash-sidebar-project-option-mark">
        <span aria-hidden="true">
          {(project.name.trim().charAt(0) || 'P').toLocaleUpperCase('es')}
        </span>
      </span>
      <span className="dash-sidebar-project-option-copy">
        <span className="dash-sidebar-project-option-name">{project.name || t('common.unnamed')}</span>
        <small>{deadline}</small>
      </span>
      <IconRotate2 size={14} aria-hidden="true" />
    </button>
  )
}

/**
 * Los borrados, guardados en una carpeta que se abre.
 *
 * Con uno solo la carpeta sobra y la fila va suelta: esconder una cosa detrás de
 * un clic no ahorra nada y sí la aleja. A partir de dos empieza a pesar —son
 * proyectos en los que ya no se puede trabajar ocupando el sitio de los que sí—
 * y entonces se recogen: un renglón que dice cuántos hay y cuándo se va el
 * primero, y sólo al abrirlo aparecen todos.
 *
 * Cerrada de salida, siempre. El desplegable se abre para cambiar de proyecto,
 * no para revisar la papelera; quien viene a por un borrado da un clic más, que
 * es exactamente el orden de frecuencias.
 */
function DeletedProjectsFolder({ projects, onOpenDeleted, onClose }: {
  projects: SidebarDeletedProject[]
  onOpenDeleted?: ((project: SidebarDeletedProject) => void) | undefined
  onClose: () => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)

  /* El plazo que se enseña plegado es el más corto: de un montón, lo que urge
     es lo primero que se pierde, no lo que quede de media. */
  const soonest = projects.reduce<SidebarDeletedProject>((closest, project) => {
    if (!project.purgeAt) return closest
    if (!closest.purgeAt) return project
    return project.purgeAt < closest.purgeAt ? project : closest
  }, projects[0]!)
  const left = daysLeft(soonest.purgeAt)
  const deadline = left === null ? t('sidebar.project.deletedFirstSoon')
    : left === 0 ? t('sidebar.project.deletedFirstToday')
      : left === 1 ? t('sidebar.project.deletedFirstOne')
        : t('sidebar.project.deletedFirstMany', { count: left })

  return (
    /* Todo el bloque en una tarjeta: el renglón que cuenta y, al abrirse, los
       proyectos que guarda. El filo es lo que dice que las dos cosas son una
       sola, y no dos filas seguidas del menú que casualmente se tocan. */
    <div className={`dash-sidebar-project-folder${open ? ' is-open' : ''}`}>
      <button
        className={`dash-sidebar-project-option is-deleted is-deleted-folder${open ? ' is-open' : ''}`}
        type="button"
        role="menuitem"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="dash-sidebar-project-option-mark" aria-hidden="true">
          <IconTrash size={13} />
        </span>
        <span className="dash-sidebar-project-option-copy">
          <span className="dash-sidebar-project-option-name">
            {t('deleted.countMany', { count: projects.length })}
          </span>
          <small>{deadline}</small>
        </span>
        <IconChevronDown className="dash-sidebar-project-folder-chevron" size={14} aria-hidden="true" />
      </button>

      {open ? (
        <div className="dash-sidebar-project-folder-body">
          {projects.map((project) => (
            <DeletedProjectRow
              key={project.id}
              project={project}
              onOpen={() => { onClose(); onOpenDeleted?.(project) }}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * El selector de proyectos compartido por la tarjeta lateral y el breadcrumb.
 *
 * El contenedor que lo abre es quien decide dónde colocarlo y cuándo cerrarlo;
 * la lista, sus caras, roles, borrados y la acción de crear viven sólo aquí.
 */
export function ProjectPickerMenu({
  projects,
  currentProjectId,
  deletedProjects = [],
  onSelectProject,
  onOpenDeleted,
  onCreateProject,
  onClose,
  className = '',
}: {
  projects: SidebarProject[]
  currentProjectId: string
  deletedProjects?: SidebarDeletedProject[]
  onSelectProject: (projectId: string) => void
  onOpenDeleted?: (project: SidebarDeletedProject) => void
  onCreateProject: () => void
  onClose: () => void
  className?: string
}) {
  const { t } = useI18n()

  return (
    <div className={`dash-sidebar-project-menu${className ? ` ${className}` : ''}`} role="menu">
      <div className="dash-sidebar-project-menu-list" role="none">
      {projects.map((project) => {
        const active = project.id === currentProjectId
        return (
          <button
            key={project.id}
            className={`dash-sidebar-project-option${active ? ' is-active' : ''}`}
            type="button"
            role="menuitem"
            onClick={() => {
              onClose()
              if (!active) onSelectProject(project.id)
            }}
          >
            <span className="dash-sidebar-project-mark-wrap">
              <ProjectFace
                name={project.name}
                {...(project.faviconLight ? { light: project.faviconLight } : {})}
                {...(project.faviconDark ? { dark: project.faviconDark } : {})}
                className="dash-sidebar-project-option-mark"
              />
              <RoleMark role={project.role} />
            </span>
            <span className="dash-sidebar-project-option-copy">
              <span className="dash-sidebar-project-option-name">{project.name}</span>
              {project.role ? (
                <small className="dash-sidebar-project-option-role">
                  {project.role === 'owner'
                    ? t('role.ownerLine')
                    : t(roleLabelKey(project.role))}
                </small>
              ) : null}
            </span>
            {active ? <IconCheck size={15} aria-hidden="true" /> : null}
          </button>
        )
      })}

      {deletedProjects.length > 0 ? (
        deletedProjects.length === 1 ? (
          /* Suelto sí lleva raya: sin tarjeta que lo encierre, nada más lo
             separaría de los proyectos vivos de arriba. */
          <>
            <div className="dash-sidebar-project-rule" />
            <DeletedProjectRow
              project={deletedProjects[0]!}
              onOpen={() => { onClose(); onOpenDeleted?.(deletedProjects[0]!) }}
            />
          </>
        ) : (
          /* La carpeta va sin raya delante: su propio borde ya la separa, y dos
             filos seguidos son una frontera dicha dos veces. */
          <DeletedProjectsFolder
            projects={deletedProjects}
            onOpenDeleted={onOpenDeleted}
            onClose={onClose}
          />
        )
      ) : null}
      </div>

      {/* Footer pegado al borde visible del menú: la lista puede desplazarse,
          pero la acción para crear un sitio siempre queda al alcance. */}
      <div className="dash-sidebar-project-create-wrap">
        <button
          /* `dash-primary-btn` es el glass del acento que ya llevan «Nueva
             reserva», «Ver planes» y el resto de acciones principales del
             panel. Esto era un macizo casi negro escrito a mano aquí: el único
             botón principal del panel que no salía del color de la marca, así
             que al cambiar el acento se quedaba solo. La clase de al lado se
             queda con lo que SÍ es de este sitio —ancho completo y el alto del
             pie del menú— y nada más. */
          className="dash-primary-btn dash-sidebar-project-create"
          type="button"
          role="menuitem"
          onClick={() => { onClose(); onCreateProject() }}
        >
          <IconPlus size={15} aria-hidden="true" />
          {t('sidebar.project.create')}
        </button>
      </div>
    </div>
  )
}

export function SidebarProjectCard({
  projects,
  currentProjectId,
  currentProjectName,
  faviconLight,
  faviconDark,
  loading = false,
  deletedProjects = [],
  onSelectProject,
  onOpenDeleted,
  onCreateProject,
}: {
  projects: SidebarProject[]
  /**
   * Los borrados que todavía se pueden recuperar.
   *
   * Van aquí y no sólo en la banda del panel porque este desplegable es donde se
   * viene a buscar un proyecto, y quien borró el suyo por error lo va a buscar
   * justo aquí. Encontrarlo en la lista, apagado y con su plazo, contesta la
   * pregunta en el sitio donde se hace.
   */
  deletedProjects?: SidebarDeletedProject[]
  /** Abrir el diálogo de «esto está borrado». No entra al proyecto. */
  onOpenDeleted?: (project: SidebarDeletedProject) => void
  currentProjectId: string
  currentProjectName: string
  /**
   * Los favicons del sitio. Sustituyen a la inicial cuando existen.
   *
   * Se pasan los dos y decide la hoja de estilos, no JavaScript: el tema vive
   * en una cookie compartida entre el panel y el editor y puede cambiarlo otra
   * pestaña, así que un valor leído una vez se queda viejo.
   */
  faviconLight?: string | undefined
  faviconDark?: string | undefined
  /** Mientras el nombre todavía no se sabe. */
  loading?: boolean
  onSelectProject: (projectId: string) => void
  /** Al asistente de creación, que vive fuera del proyecto. */
  onCreateProject: () => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const currentRole = projects.find((project) => project.id === currentProjectId)?.role

  return (
    <div className="dash-sidebar-project" ref={rootRef}>
      <button
        ref={triggerRef}
        className="dash-sidebar-project-trigger"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('sidebar.project.aria', { name: currentProjectName })}
        onClick={() => setOpen((current) => !current)}
      >
        {/* La marca de rol cuelga por fuera de la cara, que no la recorta. */}
        <span className="dash-sidebar-project-mark-wrap">
          <ProjectFace
            name={currentProjectName}
            {...(faviconLight ? { light: faviconLight } : {})}
            {...(faviconDark ? { dark: faviconDark } : {})}
            className="dash-sidebar-project-mark"
          />
          <RoleMark role={currentRole} />
        </span>
        <span className="dash-sidebar-project-copy">
          <small>{t('sidebar.project.label')}</small>
          {loading && !currentProjectName
            ? <span className="sk dash-sidebar-project-skeleton" aria-hidden="true" />
            : <strong>{currentProjectName || t('common.unnamed')}</strong>}
        </span>
        {/* Las dos flechas y no una: un chevron dice «se despliega hacia abajo»
            y esto se abre hacia arriba. El selector dice «se cambia». */}
        <IconSelector size={15} aria-hidden="true" />
      </button>

      {open ? (
        /* El mismo selector que abre el breadcrumb; aquí sólo cambia el ancla,
           por eso la colocación hacia arriba vive en la clase base. */
        <ProjectPickerMenu
          projects={projects}
          currentProjectId={currentProjectId}
          deletedProjects={deletedProjects}
          onSelectProject={onSelectProject}
          {...(onOpenDeleted ? { onOpenDeleted } : {})}
          onCreateProject={onCreateProject}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  )
}
